import styled from '@emotion/styled';
import { forwardRef } from 'react';

import type {
  CardProps,
  CardHeaderProps,
  CardMediaProps,
  CardContentProps,
  CardActionsProps,
} from './Card.types';
import type { Theme } from '../../theme';

const StyledCard = styled.div<{
  variant: 'elevated' | 'outlined' | 'filled';
  interactive: boolean;
  selected: boolean;
  padding: 'none' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.sm};
  background-color: ${({ theme }) => (theme as Theme).colors.background.default};
  transition: ${({ theme }) => (theme as Theme).transitions.fast};
  overflow: hidden;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'default')};

  ${({ variant, theme }) =>
    variant === 'elevated' &&
    `
    box-shadow: ${(theme as Theme).shadows.card};
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
  `}

  ${({ variant, theme }) =>
    variant === 'outlined' &&
    `
    border: ${(theme as Theme).borders.width.thin} solid ${(theme as Theme).colors.border.medium};
    box-shadow: none;
    
    &:hover {
      border-color: ${(theme as Theme).colors.border.input};
    }
  `}

  ${({ variant, theme }) =>
    variant === 'filled' &&
    `
    background-color: ${(theme as Theme).colors.border.light};
    box-shadow: none;
  `}

  ${({ interactive, disabled, theme }) =>
    interactive &&
    !disabled &&
    `
    cursor: pointer;
    
    &:hover {
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:focus-visible {
      outline: 2px solid ${(theme as Theme).colors.highlight};
      outline-offset: 2px;
    }
  `}

  ${({ selected, theme }) =>
    selected &&
    `
    border: 2px solid ${(theme as Theme).colors.highlight};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${(theme as Theme).colors.highlight} 20%, transparent);
  `}

  ${({ padding, theme }) =>
    padding === 'sm' &&
    `
    padding: ${(theme as Theme).spacing[3]};
  `}

  ${({ padding, theme }) =>
    padding === 'md' &&
    `
    padding: ${(theme as Theme).spacing[6]};
  `}

  ${({ padding, theme }) =>
    padding === 'lg' &&
    `
    padding: ${(theme as Theme).spacing[7]};
  `}
`;

const StyledHeader = styled.div`
  padding: ${({ theme }) => (theme as Theme).spacing[6]};
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xl};
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.semibold};
  color: ${({ theme }) => (theme as Theme).colors.text.primary};
  border-bottom: ${({ theme }) => (theme as Theme).borders.width.thin} solid ${({ theme }) => (theme as Theme).colors.border.medium};
`;

const StyledMedia = styled.div<{
  aspectRatio: '1:1' | '4:3' | '16:9' | '3:2';
}>`
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => (theme as Theme).colors.border.light};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  ${({ aspectRatio }) => {
    const ratioMap = {
      '1:1': '1 / 1',
      '4:3': '4 / 3',
      '16:9': '16 / 9',
      '3:2': '3 / 2',
    };
    return `aspect-ratio: ${ratioMap[aspectRatio]};`;
  }}
`;

const StyledContent = styled.div`
  padding: ${({ theme }) => (theme as Theme).spacing[6]};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  flex: 1;
`;

const StyledActions = styled.div<{
  align: 'left' | 'right' | 'center' | 'space-between';
}>`
  display: flex;
  gap: ${({ theme }) => (theme as Theme).spacing[3]};
  padding: ${({ theme }) => (theme as Theme).spacing[6]};
  border-top: ${({ theme }) => (theme as Theme).borders.width.thin} solid ${({ theme }) => (theme as Theme).colors.border.light};

  ${({ align }) => {
    const alignMap: Record<string, string> = {
      left: 'flex-start',
      right: 'flex-end',
      center: 'center',
      'space-between': 'space-between',
    };
    return `justify-content: ${alignMap[align]};`;
  }}
`;

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'elevated',
      interactive = false,
      selected = false,
      disabled = false,
      padding = 'none',
      onClick,
      onDoubleClick,
      className = '',
      style,
      role,
      'aria-label': ariaLabel,
      'aria-disabled': ariaDisabled,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (onClick && interactive && !disabled) {
        onClick(event);
      }
    };

    const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (onDoubleClick && interactive && !disabled) {
        onDoubleClick(event);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (interactive && !disabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    return (
      <StyledCard
        ref={ref}
        variant={variant}
        interactive={interactive}
        selected={selected}
        disabled={disabled}
        padding={padding}
        className={className}
        style={style}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        role={role || (interactive ? 'button' : undefined)}
        tabIndex={interactive && !disabled ? 0 : undefined}
        aria-label={ariaLabel}
        aria-disabled={ariaDisabled || disabled}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ children, className = '', 'data-testid': testId, dataTestId, dataId }, ref) => {
    return (
      <StyledHeader
        ref={ref}
        className={className}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {children}
      </StyledHeader>
    );
  }
);

CardHeader.displayName = 'Card.Header';

const CardMedia = forwardRef<HTMLDivElement, CardMediaProps>(
  (
    {
      src,
      alt,
      aspectRatio = '16:9',
      className = '',
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    return (
      <StyledMedia
        ref={ref}
        aspectRatio={aspectRatio}
        className={className}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        <img src={src} alt={alt} loading="lazy" />
      </StyledMedia>
    );
  }
);

CardMedia.displayName = 'Card.Media';

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ children, className = '', 'data-testid': testId, dataTestId, dataId }, ref) => {
    return (
      <StyledContent
        ref={ref}
        className={className}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {children}
      </StyledContent>
    );
  }
);

CardContent.displayName = 'Card.Content';

const CardActions = forwardRef<HTMLDivElement, CardActionsProps>(
  (
    {
      children,
      align = 'right',
      className = '',
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    return (
      <StyledActions
        ref={ref}
        align={align}
        className={className}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {children}
      </StyledActions>
    );
  }
);

CardActions.displayName = 'Card.Actions';

const CardWithCompounds = Object.assign(Card, {
  Header: CardHeader,
  Media: CardMedia,
  Content: CardContent,
  Actions: CardActions,
});

export default CardWithCompounds;